'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _extends = require('@babel/runtime/helpers/extends');
var React = require('react');
var client = require('@apollo/client');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var nthHookSettings = require('@narvar/nth-hook-settings');
var nthHookGlobalSettings = require('@narvar/nth-hook-global-settings');
var useOrderTracking = require('@narvar/nth-hook-order-tracking');
var NarvarGrowthBookProvider = require('./NarvarGrowthBookProvider.js');
var nthHookContextAnalytics = require('@narvar/nth-hook-context-analytics');
var Nested = require('./Nested.js');
var useTitanAnalytics = require('./useTitanAnalytics.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var _extends__default = /*#__PURE__*/_interopDefaultLegacy(_extends);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var useOrderTracking__default = /*#__PURE__*/_interopDefaultLegacy(useOrderTracking);

let Product = /*#__PURE__*/function (Product) {
  Product["narvarjs"] = "narvarjs";
  Product["narvarjsBuild"] = "narvarjs_build";
  Product["narvarjsTranslations"] = "narvarjs_translations";
  return Product;
}({});
let ExperienceHostEnum = /*#__PURE__*/function (ExperienceHostEnum) {
  ExperienceHostEnum["NARVAR"] = "NARVAR";
  ExperienceHostEnum["EMBED"] = "EMBED";
  return ExperienceHostEnum;
}({});
let StatusCode = /*#__PURE__*/function (StatusCode) {
  StatusCode["SHPD"] = "SHPD";
  StatusCode["INTR"] = "INTR";
  StatusCode["OFDL"] = "OFDL";
  StatusCode["DLVD"] = "DLVD";
  StatusCode["DLPP"] = "DLPP";
  StatusCode["PUBC"] = "PUBC";
  StatusCode["ITTS"] = "ITTS";
  StatusCode["ITPP"] = "ITPP";
  StatusCode["DLTS"] = "DLTS";
  StatusCode["EXCP"] = "EXCP";
  StatusCode["UNDL"] = "UNDL";
  StatusCode["BOSS"] = "BOSS";
  StatusCode["BOPIS"] = "BOPIS";
  StatusCode["UNKN"] = "UNKN";
  return StatusCode;
}({});
let LineStatus = /*#__PURE__*/function (LineStatus) {
  LineStatus["SUCCESS"] = "SUCCESS";
  LineStatus["FAILURE"] = "FAILURE";
  return LineStatus;
}({});
const DEFAULT_PAGE_CONTEXT = {
  product: Product.narvarjs,
  experienceHost: 'NARVAR',
  embedReferrerUrl: undefined,
  retailerMoniker: 'DEFAULT_RETAILER',
  versionName: 'narvar',
  isFallbackView: false,
  segmentId: undefined,
  segmentName: undefined,
  localeLanguage: undefined,
  localeCountry: undefined,
  type: undefined,
  trackingType: undefined,
  category: undefined,
  trackingStatus: undefined,
  trackingStatusCode: undefined,
  carrier: undefined,
  trackingNumbers: undefined,
  orderNumbers: undefined,
  requestUrl: undefined,
  destinationZip: undefined,
  originZip: undefined,
  originCountry: undefined,
  destinationCountry: undefined,
  service: undefined,
  orderDate: undefined,
  shipDate: undefined,
  retailerDcId: undefined,
  productCategory: undefined,
  promiseDate: undefined,
  orderApiEnabled: undefined,
  secured: undefined,
  items: undefined,
  itemIds: undefined,
  itemNumber: undefined,
  productSkus: undefined,
  mainShipmentId: undefined,
  email: undefined,
  phone: undefined,
  cache: undefined,
  orderType: undefined,
  token: undefined,
  bannerName: undefined,
  bannerSegmentId: undefined,
  bannerSegmentName: undefined,
  lineStatus: undefined,
  claimPortalUrl: undefined,
  customerMarketingAttributes: undefined,
  previewDate: undefined
};

/**
 * orderData being included for mustache templating purposes, particularly
 * in custom components
 */
const getFlatOrderData = (itemIdsParameter, orderTrackingData) => {
  const items = (orderTrackingData?.orders?.[0]?.shipments || []).flatMap(shipment => shipment.items);
  const {
    productSkus,
    itemIds
  } = items.reduce((acc, item) => {
    if (item.productSku) acc.productSkus.push(item.productSku);
    if (item.itemId) acc.itemIds.push(item.itemId);
    return acc;
  }, {
    productSkus: [],
    itemIds: []
  });
  const customerMarketingAttributes = orderTrackingData?.orders?.[0]?.customerMarketingAttributes;
  return {
    items,
    productSkus,
    itemIds: itemIdsParameter?.length ? itemIdsParameter : itemIds,
    customerMarketingAttributes: customerMarketingAttributes ? {
      billingZipSha256: customerMarketingAttributes?.billingZipSha256,
      firstNameSha256: customerMarketingAttributes?.firstNameSha256,
      lastNameSha256: customerMarketingAttributes?.lastNameSha256,
      emailSha256: customerMarketingAttributes?.emailSha256
    } : undefined
  };
};
const formatPageContext = (oldState, orderTrackingData) => {
  return {
    ...oldState,
    ...getFlatOrderData(oldState.itemIds, orderTrackingData)
  };
};
const PageContextContext = /*#__PURE__*/React.createContext({
  state: DEFAULT_PAGE_CONTEXT,
  update: () => {}
});
const OrderTrackingDataDependentProviders = ({
  analytics = nthHookContextAnalytics.DEFAULT_ANALYTICS_CONTEXT,
  children,
  initialState = DEFAULT_PAGE_CONTEXT,
  updateInitialState
}) => {
  const {
    getCurrentShipment,
    query
  } = useOrderTracking__default["default"]();
  const orderTrackingData = query?.data?.orderTracking;
  const currentShipment = getCurrentShipment();
  const orderNumber = initialState?.orderNumbers?.[0] ??
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  orderTrackingData?.orders?.[0]?.tokenPayload?.['order_number'] ?? undefined;
  const pageContextState = formatPageContext({
    ...initialState,
    orderNumbers: orderNumber ? [orderNumber] : undefined,
    orderApiEnabled: orderTrackingData?.orderApiEnabled,
    mainShipmentId: currentShipment?.shipmentId,
    trackingNumbers: currentShipment?.trackingNumber ? [currentShipment.trackingNumber] : undefined,
    trackingStatus: currentShipment?.status || undefined,
    trackingStatusCode: currentShipment?.statusCode || undefined,
    carrier: currentShipment?.carrier || undefined,
    isFallbackView: currentShipment?.isFallback || false,
    orderType: currentShipment?.type || undefined
  }, orderTrackingData);
  const analyticsState = {
    ...analytics,
    data: {
      tag: analytics.data.tag,
      eventSchema: analytics.data.eventSchema,
      productName: analytics.data.productName,
      pageRetailerMoniker: analytics.data.pageRetailerMoniker,
      orderNumber,
      pageOrderNumber: orderNumber,
      bannerSegmentId: initialState.bannerSegmentId,
      bannerSegmentName: initialState.bannerSegmentName,
      pageLocaleCountry: initialState.localeCountry,
      pageLocaleLanguage: initialState.localeLanguage,
      pageTrackType: initialState.trackingType,
      pageUrlLocale: initialState.localeLanguage && initialState.localeCountry && `${initialState.localeLanguage}_${initialState.localeCountry}`,
      pageVersionName: initialState.versionName,
      segmentId: initialState.segmentId,
      segmentName: initialState.segmentName,
      eventCarrierMoniker: currentShipment?.carrier,
      eventTrackingNumber: currentShipment?.trackingNumber,
      eventTrackStatus: currentShipment?.status,
      eventTrackSubStatus: currentShipment?.statusCode,
      isFallbackView: currentShipment?.isFallback,
      pageShipmentStatus: currentShipment?.status
    }
  };
  if (query?.networkStatus === client.NetworkStatus.loading) return null;
  return /*#__PURE__*/React__default["default"].createElement(Nested["default"], {
    elements: [
    /*#__PURE__*/
    /* eslint-disable react/jsx-key */
    React__default["default"].createElement(PageContextContext.Provider, {
      value: {
        update: updateInitialState,
        state: pageContextState
      }
    }), /*#__PURE__*/React__default["default"].createElement(nthHookContextAnalytics.AnalyticsProvider, {
      value: analyticsState
    }), /*#__PURE__*/React__default["default"].createElement(nthHookTheme.LomaThemeConfigProvider, {
      product: "narvarjs",
      retailerMoniker: pageContextState.retailerMoniker,
      versionName: pageContextState.versionName,
      localeLanguage: pageContextState.localeLanguage,
      localeCountry: pageContextState.localeCountry,
      bannerName: pageContextState.bannerName,
      cache: pageContextState.cache
    }), /*#__PURE__*/React__default["default"].createElement(nthHookTranslations.LomaTranslationsConfigProvider, {
      product: "narvarjs_translations",
      retailerMoniker: pageContextState.retailerMoniker,
      versionName: pageContextState.versionName,
      localeLanguage: pageContextState.localeLanguage,
      localeCountry: pageContextState.localeCountry,
      category: pageContextState.trackingStatusCode,
      status: pageContextState.trackingStatus,
      type: pageContextState.trackingType,
      cache: pageContextState.cache
    }), /*#__PURE__*/React__default["default"].createElement(nthHookSettings.LomaSettingsConfigProvider, {
      product: "narvarjs",
      retailerMoniker: pageContextState.retailerMoniker,
      versionName: pageContextState.versionName,
      localeLanguage: pageContextState.localeLanguage,
      localeCountry: pageContextState.localeCountry,
      bannerName: pageContextState.bannerName,
      status: pageContextState.trackingStatus
      // the only component that needs category is the track page layout, and
      // it's required to pass Device enum as the value. initializing this here as
      // undefined prevents any other initial request from passing a bad category.
      // TODO: we need to either decouple pageContext from settings queries or find
      // a separate way to pass a descriptor to fetch the correct layout
      ,
      category: undefined,
      cache: pageContextState.cache
    }), /*#__PURE__*/React__default["default"].createElement(nthHookGlobalSettings.LomaGlobalSettingsConfigProvider, {
      retailerMoniker: pageContextState.retailerMoniker,
      localeLanguage: pageContextState.localeLanguage,
      localeCountry: pageContextState.localeCountry,
      cache: pageContextState.cache
    })
    /* eslint-enable react/jsx-key */]
  }, children);
};
const PageProvider = ({
  analytics,
  apollo,
  children,
  growthbook,
  titanAnalyticsOptions,
  initialState: initialStateProp,
  onUnauthenticatedRequest,
  deviceType
}) => {
  const [initialState, setInitialState] = React.useState(initialStateProp);
  const update = React.useCallback(next => {
    setInitialState(next);
  }, []);
  return /*#__PURE__*/React__default["default"].createElement(Nested["default"]
  /* eslint-disable react/jsx-key */, {
    elements: [apollo &&
    /*#__PURE__*/
    // ApolloProvider forces us to specify a children property
    // eslint-disable-next-line react/no-children-prop
    React__default["default"].createElement(client.ApolloProvider, _extends__default["default"]({}, apollo, {
      children: ""
    })), /*#__PURE__*/React__default["default"].createElement(useOrderTracking.OrderTrackingProvider, {
      value: {
        onUnauthenticatedRequest,
        queryVariables: {
          retailerMoniker: initialState?.retailerMoniker || '',
          trackingNumbers: initialState?.trackingNumbers,
          orderNumbers: initialState?.orderNumbers,
          versionName: initialState?.versionName,
          carrier: initialState?.carrier,
          type: initialState?.type,
          orderType: initialState?.orderType,
          orderApiEnabled: initialState?.orderApiEnabled,
          localeCountry: initialState?.localeCountry,
          localeLanguage: initialState?.localeLanguage,
          destinationZip: initialState?.destinationZip,
          email: initialState?.email,
          phone: initialState?.phone,
          secured: initialState?.secured,
          originZip: initialState?.originZip,
          originCountry: initialState?.originCountry,
          destinationCountry: initialState?.destinationCountry,
          service: initialState?.service,
          orderDate: initialState?.orderDate,
          shipDate: initialState?.shipDate,
          retailerDcId: initialState?.retailerDcId,
          productCategory: initialState?.productCategory,
          promiseDate: initialState?.promiseDate,
          mainShipmentId: initialState?.mainShipmentId,
          token: initialState?.token,
          cache: initialState?.cache,
          previewDate: initialState?.previewDate,
          experienceHost: initialState?.experienceHost,
          url: initialState?.requestUrl || ''
        }
      }
    }), /*#__PURE__*/React__default["default"].createElement(OrderTrackingDataDependentProviders, {
      initialState: initialState,
      updateInitialState: update,
      analytics: analytics
    }),
    /*#__PURE__*/
    // usePageContext is now available at this point
    React__default["default"].createElement(useTitanAnalytics.TitanAnalyticsProvider, {
      value: titanAnalyticsOptions
    }), /*#__PURE__*/React__default["default"].createElement(NarvarGrowthBookProvider["default"], {
      growthbookOptions: growthbook,
      deviceType: deviceType
    })]
    /* eslint-enable react/jsx-key */
  }, /*#__PURE__*/React__default["default"].createElement(nthHookContextAnalytics.WebVitals, null), children);
};
const usePageContext = () => React.useContext(PageContextContext);

exports.growthbookAttributes = NarvarGrowthBookProvider.growthbookAttributes;
exports.useTitanAnalytics = useTitanAnalytics["default"];
exports.DEFAULT_PAGE_CONTEXT = DEFAULT_PAGE_CONTEXT;
exports.ExperienceHostEnum = ExperienceHostEnum;
exports.LineStatus = LineStatus;
exports.PageProvider = PageProvider;
exports.Product = Product;
exports.StatusCode = StatusCode;
exports.formatPageContext = formatPageContext;
exports.usePageContext = usePageContext;
